import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, ClipboardCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const empty = { phone: "", email: "", level: "", notes: "" };

export function ProposeRefereeChangeDialog({ referee, onClose, onSubmitted }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(empty); }, [referee?.id]);

  const canSubmit = !!(form.phone.trim() || form.email.trim() || form.level.trim());

  const submit = async () => {
    if (!canSubmit) { toast.error("Enter at least one new detail"); return; }
    setBusy(true);
    try {
      await api.post("/proposals/referee-change", {
        referee_id: referee.id,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        level: form.level.trim() || null,
        notes: form.notes.trim() || null,
      });
      toast.success("Change request sent — admins will review it");
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send the change request");
    } finally { setBusy(false); }
  };

  const Field = ({ label, k, type = "text", placeholder }) => (
    <div>
      <Label className="text-[10px] tracking-widest uppercase opacity-70">{label}</Label>
      <div className="text-[11px] opacity-50 mb-1">Currently: {referee?.[k] || "—"}</div>
      <Input type={type} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} placeholder={placeholder} data-testid={`ref-change-${k}`} />
    </div>
  );

  return (
    <Dialog open={!!referee} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="propose-referee-change-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl flex items-center gap-2">
            <Pencil className="w-5 h-5 text-[#83B68B]" /> Suggest changes · {referee?.name}
          </DialogTitle>
          <DialogDescription className="text-xs opacity-70">
            Only fill in the details that need updating. An admin will review before the directory changes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <Field label="New phone" k="phone" placeholder="07…" />
          <Field label="New email" k="email" type="email" placeholder="ref@example.com" />
          <Field label="New level / experience" k="level" placeholder="FA Level 4 · 3 years" />
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Why the change?</Label>
            <Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Ref texted me from a new number on Sunday" data-testid="ref-change-notes" />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-md border border-border text-xs tracking-widest uppercase font-display font-bold hover:border-white/40" data-testid="ref-change-cancel">Not now</button>
            <button type="button" onClick={submit} disabled={!canSubmit || busy} data-testid="ref-change-submit" className="btn-volt flex-1 py-3 rounded-md disabled:opacity-40 inline-flex items-center justify-center gap-2">
              <ClipboardCheck className="w-4 h-4" /> {busy ? "Sending…" : "Send for review"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
